import apiClient from '../client';

/**
 * @typedef {Object} Campaign
 * @property {string} id
 * @property {string} name
 * @property {string} status - DRAFT | SENDING | PAUSED | COMPLETED | ARCHIVED
 * @property {Object} counts - { total, pending, sent, failed, responded }
 * @property {Object} settings - { minDelay, maxDelay, dailyLimit }
 */

/**
 * @typedef {Object} CampaignDetails
 * @property {Campaign} campaign
 * @property {Array} recipients
 */

/**
 * Получить список всех кампаний с метаданными
 * @param {Object} [filters] - { owner, teamId, managerId }
 * @returns {Promise<{ items: Campaign[], userRole?: string, teams?: Array, managers?: Array }>}
 */
export async function getCampaigns(filters = {}) {
  const { owner = 'all', teamId, managerId } = filters;
  const { data } = await apiClient.get('/outreach/campaigns', {
    params: { owner, teamId, managerId },
    headers: { 'Cache-Control': 'no-cache' }
  });
  return {
    items: data?.items || [],
    userRole: data?.userRole,
    teams: data?.teams,
    managers: data?.managers
  };
}

/**
 * Получить детали кампании с получателями
 * @param {string} campaignId
 * @returns {Promise<CampaignDetails>} - { campaign, recipients, stats, ... }
 */
export async function getCampaignDetails(campaignId) {
  const { data } = await apiClient.get(`/outreach/campaigns/${campaignId}`);
  return {
    ...data,
    campaign: data.campaign,
    recipients: data.recipients || []
  };
}

/**
 * Создать новую кампанию
 * @param {Object} params
 * @param {string} params.name
 * @param {string} [params.description]
 * @param {string} [params.templateId]
 * @returns {Promise<Campaign>}
 */
export async function createCampaign({ name, description, templateId, provider, replyScheduleId, emailAccountIds, steps, recipients, start }) {
  const payload = { name, description, templateId };
  if (provider) payload.provider = provider;
  if (provider === 'replyio') {
    if (replyScheduleId !== undefined) payload.replyScheduleId = replyScheduleId;
    if (emailAccountIds !== undefined) payload.emailAccountIds = emailAccountIds;
    if (steps !== undefined) payload.steps = steps;
    if (recipients !== undefined) payload.recipients = recipients;
    if (start !== undefined) payload.start = start;
  }
  const { data } = await apiClient.post('/outreach/campaigns', payload);
  return data.campaign;
}

/**
 * Derive the provider for a campaign row: 'replyio' when replySequenceId is set, else null.
 */
export function getCampaignProvider(camp) {
  if (!camp) return null;
  if (camp.replySequenceId) return 'replyio';
  return null;
}

/**
 * Дублировать кампанию (с контактами)
 * @param {string} campaignId
 * @returns {Promise<Campaign>}
 */
export async function duplicateCampaign(campaignId) {
  const { data } = await apiClient.post(`/outreach/campaigns/${campaignId}/duplicate`);
  return data.campaign;
}

/**
 * Обновить кампанию
 * @param {string} campaignId
 * @param {Object} updates
 * @returns {Promise<Campaign>}
 */
export async function updateCampaign(campaignId, updates) {
  const { data } = await apiClient.patch(`/outreach/campaigns/${campaignId}`, updates);
  return data.campaign;
}

/**
 * Переключить pre-send комплимент кампании.
 *
 * Отдельный эндпоинт (PATCH /outreach/campaigns/:id/compliment-gate) проверяет только
 * доступ к кампании, без write-гарда updateCampaign (PATCH /:id, requireCampaignWrite).
 * Принимает только requiresCompliment + complimentPromptOverride.
 *
 * @param {string} campaignId
 * @param {{ requiresCompliment?: boolean, complimentPromptOverride?: string|null }} gate
 * @returns {Promise<Campaign>}
 */
export async function updateCampaignComplimentGate(campaignId, gate) {
  const { data } = await apiClient.patch(`/outreach/campaigns/${campaignId}/compliment-gate`, gate);
  return data.campaign;
}

/**
 * Обновить список менеджеров с доступом к кампании.
 *
 * PATCH /api/outreach/campaigns/:id/allowed-managers
 * - replaces the stored manager allow-list with `managerIds`; an empty array clears it
 *
 * @param {string} campaignId
 * @param {string[]} managerIds  - selected manager IDs (may be empty)
 * @returns {Promise<{ ok: boolean, campaign: { id: string, allowedManagerIds: string[] } }>}
 */
export async function updateAllowedManagers(campaignId, managerIds) {
  const { data } = await apiClient.patch(
    `/outreach/campaigns/${campaignId}/allowed-managers`,
    { managerIds }
  );
  return data;
}

/**
 * Передать владельца Reply-кампании другому менеджеру (та же команда).
 * Бэкенд вызовет Reply sequences.changeOwner + обновит CRM ownerUserId.
 */
export async function transferCampaignOwner(campaignId, ownerUserId) {
  const { data } = await apiClient.patch(
    `/outreach/campaigns/${campaignId}/owner`,
    { ownerUserId }
  );
  return data;
}

/**
 * Подтянуть содержимое Reply sequence (steps + emailAccounts + settings) live.
 * Не кешируется на сервере; UI кешит через react-query.
 */
export async function fetchReplySequence(campaignId) {
  const { data } = await apiClient.get(`/outreach/campaigns/${campaignId}/reply-sequence`);
  return data;
}

/**
 * Добавить лида в stage-1 кампанию (без ручной отправки email из чата).
 * Backend сам подберёт доступную кампанию, если campaignId не передан.
 *
 * @param {string} leadId
 * @param {string | null} [campaignId]
 * @returns {Promise<{ ok: boolean, campaignId?: string, campaignName?: string, recipientId?: string, recipientStatus?: string }>}
 */
export async function enrollLeadToStageOneCampaign(leadId, campaignId = null) {
  const payload = { leadId };
  if (campaignId) payload.campaignId = campaignId;
  const { data } = await apiClient.post('/outreach/campaigns/stage-1/enroll', payload);
  return data;
}

/**
 * Batch-enroll multiple leads into a stage-1 campaign (one HTTP call).
 *
 * @param {string[]} leadIds
 * @param {string} campaignId
 * @returns {Promise<{ ok: boolean, results: Array, counts: { enrolled: number, failed: number, notInSequence: number } }>}
 */
export async function enrollLeadsBatch(leadIds, campaignId) {
  const { data } = await apiClient.post('/outreach/campaigns/stage-1/enroll-batch', { leadIds, campaignId });
  return data;
}

/**
 * Удалить лида из выбранной кампании stage-1.
 *
 * @param {string} leadId
 * @param {string} campaignId
 * @returns {Promise<{ ok: boolean, removed?: boolean }>}
 */
export async function removeLeadFromStageOneCampaign(leadId, campaignId) {
  const { data } = await apiClient.delete('/outreach/campaigns/stage-1/enroll', {
    data: { leadId, campaignId }
  });
  return data;
}

/**
 * Получить кампании для stage-1 и текущие membership связи по лиду.
 *
 * @param {string} leadId
 * @returns {Promise<{ ok: boolean, selectedCampaignId: string | null, items: Array }>}
 */
export async function getStageOneCampaignOptions(leadId) {
  const { data } = await apiClient.get(`/outreach/campaigns/stage-1/options/${leadId}`);
  return {
    ok: Boolean(data?.ok),
    selectedCampaignId: data?.selectedCampaignId || null,
    items: Array.isArray(data?.items) ? data.items : []
  };
}

/**
 * Синхронизировать membership лида по кампаниям.
 *
 * @param {string} leadId
 * @returns {Promise<{ ok: boolean, synced: number }>}
 */
export async function syncLeadStageOneCampaignMemberships(leadId) {
  const { data } = await apiClient.post(`/outreach/campaigns/stage-1/sync/${leadId}`);
  return {
    ok: Boolean(data?.ok),
    synced: Number(data?.synced || 0)
  };
}

/**
 * Получить список лидов кампании.
 *
 * @param {string} campaignId
 * @param {Object} [filters]
 * @param {string} [filters.managerId]              filter by lead.ownerId
 * @param {string} [filters.teamId]                 filter by user.teamId
 * @param {boolean} [filters.hasReply]              only show leads with replied=true (mirror)
 * @param {string[]} [filters.groupBy]              subset of ['manager','team','stage'] — order = nesting
 * @param {string} [filters.managerFilter]          alias of managerId
 * @returns {Promise<{
 *   ok?: boolean,
 *   leads?: Array,
 *   groups?: Array,
 *   stats?: { people: number, replyRate: number, progress: number, deliveries: number },
 *   managerOptions: Array,
 *   teamOptions: Array,
 * }>}
 */
export async function getCampaignLeads(campaignId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.managerId) params.set('managerId', filters.managerId);
  if (filters.teamId) params.set('teamId', filters.teamId);
  if (filters.hasReply) params.set('hasReply', '1');
  if (filters.groupBy && filters.groupBy.length) {
    params.set('groupBy', filters.groupBy.join(','));
  }
  if (!filters.managerId && filters.managerFilter) {
    params.set('managerFilter', filters.managerFilter);
  }
  const qs = params.toString();
  const { data } = await apiClient.get(
    `/outreach/campaigns/${campaignId}/leads${qs ? '?' + qs : ''}`
  );
  return {
    ok: data?.ok,
    leads: data?.leads,
    groups: data?.groups,
    stats: data?.stats,
    managerOptions: data?.managerOptions || [],
    teamOptions: data?.teamOptions || [],
  };
}

/**
 * Получить расписание следующей отправки
 * @param {string} campaignId
 * @returns {Promise<{ nextScheduledAt: string | null }>}
 */
export async function getCampaignSchedule(campaignId) {
  const { data } = await apiClient.get(`/outreach/campaigns/${campaignId}/schedule`);
  return data;
}

/**
 * Update an email step variant's content (subject + message).
 * Calls PUT /api/outreach/campaigns/:id/sequence/steps/:stepId/variants/:variantId.
 * Backend proxies to Reply PUT /v3/sequences/{seqId}/steps/{stepId}/variants/{vid}.
 *
 * Reply requires `message` (not `body`); subject is optional and nullable.
 *
 * @param {string} campaignId
 * @param {number|string} stepId    - Reply step ID
 * @param {number|string} variantId - Reply variant ID
 * @param {{ subject?: string|null, message: string }} payload
 * @returns {Promise<{ ok: boolean, variant: { id, subject, message, isEnabled } }>}
 */
export async function updateSequenceVariant(campaignId, stepId, variantId, { subject, message }) {
  const { data } = await apiClient.put(
    `/outreach/campaigns/${campaignId}/sequence/steps/${stepId}/variants/${variantId}`,
    { subject: subject ?? null, message }
  );
  return data;
}

/**
 * Fetch the last 5 owner transfer history rows for a campaign.
 * Calls GET /api/outreach/campaigns/:id/owner-history.
 * Used by the Settings tab to display the audit log of ownership changes.
 *
 * @param {string} campaignId
 * @returns {Promise<{ ok: boolean, items: Array<{ id, campaignId, oldOwnerId, newOwnerId, changedBy, changedAt }> }>}
 */
export async function fetchOwnerHistory(campaignId) {
  const { data } = await apiClient.get(`/outreach/campaigns/${campaignId}/owner-history`);
  return data;
}

/**
 * Подтянуть активные кампании из Reply.io (ADMIN/TEAM_LEAD).
 * @returns {Promise<{ok, perTeam, totals, skipped, note?}>}
 */
export async function importCampaignsFromReply() {
  const { data } = await apiClient.post('/outreach/campaigns/import-from-reply');
  return data;
}
